import React, { Fragment, useState, useEffect, useCallback } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import {
  GetBuyerInformation,
  SaveBuyerInformation,
  UpdateBuyerInformation,
  DeleteBuyerInformation,
} from "../utils/service";
import { SITENAMEALIAS } from "../utils/init";
import { currentPageAccess } from "../utils/library";

// ─── Constants ───────────────────────────────────────────────────────────────
const getMerchantId = () =>
  localStorage.getItem(SITENAMEALIAS + "_merchant_id") || "";

const STD_FIELD_MAP = [
  {
    key: "email",
    apiKey: "collectEmail",
    label: "Email Address",
    icon: "mdi-email",
    locked: true,
    helpText: "Used for order confirmation",
  },
  {
    key: "full_name",
    apiKey: "collectFullName",
    label: "Full Name",
    icon: "mdi-account",
    locked: false,
    helpText: "Buyer's full legal name (optional)",
  },
  {
    key: "address",
    apiKey: "collectAddress",
    label: "Shipping Address",
    icon: "mdi-map-marker",
    locked: false,
    helpText: "Street, city, state, country, ZIP",
  },
  {
    key: "phone",
    apiKey: "collectPhoneNumber",
    label: "Phone Number",
    icon: "mdi-phone",
    locked: false,
    helpText: "Contact number for delivery",
  },
  {
    key: "company",
    apiKey: "collectCompanyName",
    label: "Company Name",
    icon: "mdi-domain",
    locked: false,
    helpText: "Business name (if applicable)",
  },
  {
    key: "tax_id",
    apiKey: "collectTaxInfo",
    label: "Tax ID / VAT Number",
    icon: "mdi-note-text",
    locked: false,
    helpText: "For business invoicing",
  },
  // {
  //   key: "refund_address",
  //   apiKey: "collectCryptoRefundAddress",
  //   label: "Crypto Refund Address",
  //   icon: "mdi-wallet",
  //   locked: false,
  //   helpText: "For potential refunds in crypto",
  // },
  {
    key: "notes",
    apiKey: "collectOrderNotes",
    label: "Order Notes",
    icon: "mdi-note-text",
    locked: false,
    helpText: "Additional instructions from buyer",
  },
];

const MAX_CUSTOM_FIELDS = 3;
const FIELD_TYPES = [
  "text",
  "number",
  "email",
  "phone",
  "date",
  "textarea",
  "dropdown",
  "checkbox",
];

// ─── Transformers ─────────────────────────────────────────────────────────────

function apiToProfile(raw) {
  const stdFields = STD_FIELD_MAP.map((f) => ({
    ...f,
    required: f.locked,
    enabled: f.locked ? true : !!raw[f.apiKey],
  }));

  // GET now returns customFields as a proper array of objects
  const customFields = Array.isArray(raw.customFields)
    ? raw.customFields.map((cf, i) => ({
        id: Date.now() + i,
        customFieldId: cf.customFieldId,
        label: cf.fieldLabel || "",
        type: cf.fieldType || "text",
        required: false,
        placeholder: cf.placeholderText || "",
        helpText: cf.helpText || "",
        options: "",
      }))
    : [];

  return {
    id: raw.id,
    merchantId: raw.merchantId,
    name: raw.profileName,
    isDefaultProfile: !!raw.isDefaultProfile,
    createdAt: raw.createdAt || null,
    stdFields,
    customFields,
  };
}

/** POST payload — CustomFieldId is capitalized */
function profileToCreatePayload(profile, merchantId) {
  const payload = {
    merchantId,
    profileName: profile.name,
    isDefaultProfile: profile.isDefaultProfile ? 1 : 0,
    customFields: [],
  };
  STD_FIELD_MAP.forEach((f) => {
    const field = profile.stdFields.find((sf) => sf.key === f.key);
    payload[f.apiKey] = field?.enabled ? 1 : 0;
  });
  profile.customFields.forEach((cf, idx) => {
    if (cf.label?.trim()) {
      payload.customFields.push({
        CustomFieldId: idx + 1, // capitalized for POST
        fieldLabel: cf.label.trim(),
        fieldType: cf.type || "text",
        placeholderText: cf.placeholder || "",
        helpText: cf.helpText || "",
      });
    }
  });
  return payload;
}

/** PUT payload — customFieldId is lowercase, includes createdAt */
function profileToUpdatePayload(profile, merchantId) {
  const payload = {
    merchantId,
    profileName: profile.name,
    isDefaultProfile: profile.isDefaultProfile ? 1 : 0,
    createdAt: profile.createdAt || null,
    customFields: [],
  };
  STD_FIELD_MAP.forEach((f) => {
    const field = profile.stdFields.find((sf) => sf.key === f.key);
    payload[f.apiKey] = field?.enabled ? 1 : 0;
  });
  profile.customFields.forEach((cf, idx) => {
    if (cf.label?.trim()) {
      payload.customFields.push({
        customFieldId: cf.customFieldId || idx + 1, // lowercase for PUT
        fieldLabel: cf.label.trim(),
        fieldType: cf.type || "text",
        placeholderText: cf.placeholder || "",
        helpText: cf.helpText || "",
      });
    }
  });
  return payload;
}

const buildDefaultStdFields = () =>
  STD_FIELD_MAP.map((f) => ({ ...f, required: f.locked, enabled: f.locked }));

// ─── Styles ──────────────────────────────────────────────────────────────────
const S = {
  pageWrapper: { padding: "36px 24px 60px" },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "28px",
    flexWrap: "wrap",
    gap: "16px",
  },
  pageTitle: {
    fontSize: "24px",
    fontWeight: "700",
    color: "var(--text-color)",
    margin: 0,
  },
  pageSubtitle: { fontSize: "13px", opacity: 0.5, margin: "4px 0 0" },
  card: {
    background: "var(--body-color-less)",
    borderRadius: "16px",
    border: "1px solid var(--body-color-less2)",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    marginBottom: "24px",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "10px 12px",
    padding: "20px 28px",
    borderBottom: "1px solid var(--body-color-less2)",
  },
  cardHeaderIcon: {
    width: "36px",
    height: "36px",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "16px",
  },
  cardHeaderTitle: {
    fontSize: "16px",
    fontWeight: "700",
    color: "var(--text-color)",
    margin: 0,
  },
  cardBody: { padding: "24px 28px" },
  formGroup: { display: "flex", flexDirection: "column", gap: "6px" },
  label: {
    fontSize: "12px",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.3px",
    color: "var(--text-color)",
    opacity: 1,
  },
  input: {
    padding: "10px 14px",
    fontSize: "14px",
    borderRadius: "10px",
    border: "1px solid var(--body-color-less2)",
    background: "var(--body-color)",
    color: "var(--text-color)",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  select: {
    padding: "10px 14px",
    fontSize: "14px",
    borderRadius: "10px",
    border: "1px solid var(--body-color-less2)",
    background: "var(--body-color)",
    color: "var(--text-color)",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
    appearance: "auto",
  },
  btnPrimary: {
    padding: "10px 24px",
    background: "var(--btn-color)",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
  },
  btnOutline: {
    padding: "8px 18px",
    background: "transparent",
    border: "1px solid var(--btn-color)",
    color: "var(--btn-color)",
    borderRadius: "10px",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
  },
  btnDanger: {
    padding: "6px 14px",
    background: "rgba(239,68,68,0.1)",
    border: "none",
    color: "#ef4444",
    borderRadius: "8px",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
  },
  btnGhost: {
    padding: "8px 16px",
    background: "transparent",
    border: "1px dashed var(--body-color-less2)",
    color: "var(--btn-color)",
    borderRadius: "10px",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
  },
  divider: { borderTop: "1px solid var(--body-color-less2)", margin: "20px 0" },
  skeleton: {
    background: "var(--body-color-less2)",
    borderRadius: "8px",
    animation: "pulse 1.5s ease-in-out infinite",
  },
  toast: (ok) => ({
    position: "fixed",
    bottom: "24px",
    right: "24px",
    zIndex: 9999,
    padding: "12px 20px",
    borderRadius: "12px",
    fontSize: "13px",
    fontWeight: "600",
    background: ok ? "#22c55e" : "#ef4444",
    color: "#fff",
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    animation: "slideUp 0.25s ease",
  }),
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9998,
  },
  confirmModal: {
    background: "var(--body-color-less)",
    borderRadius: "16px",
    padding: "28px",
    width: "360px",
    boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
  },
};

// ─── Sub-components ───────────────────────────────────────────────────────────
function Toggle({ value, onChange }) {
  return (
    <div
      onClick={() => onChange(!value)}
      style={{
        width: "36px",
        height: "20px",
        borderRadius: "10px",
        background: value ? "var(--btn-color)" : "var(--body-color-less2)",
        position: "relative",
        transition: "background 0.2s",
        cursor: "pointer",
        flexShrink: 0,
      }}
    >
      <div
        style={{
          width: "14px",
          height: "14px",
          borderRadius: "50%",
          background: "#fff",
          position: "absolute",
          top: "3px",
          left: value ? "19px" : "3px",
          transition: "left 0.2s",
          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
        }}
      />
    </div>
  );
}

function Toast({ message, ok, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div style={S.toast(ok)}>
      <i
        className={`mdi ${
          ok ? "mdi-check-circle" : "mdi-alert-circle"
        }`}
      />
      {message}
    </div>
  );
}

/** Inline confirm dialog — avoids browser alert() */
function ConfirmDialog({ title, message, onConfirm, onCancel, confirming }) {
  return (
    <div style={S.overlay}>
      <div style={S.confirmModal}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "16px",
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "12px",
              background: "rgba(239,68,68,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <i
              className="mdi mdi-delete"
              style={{ color: "#ef4444", fontSize: "20px" }}
            />
          </div>
          <div>
            <div
              style={{
                fontSize: "15px",
                fontWeight: "700",
                color: "var(--text-color)",
              }}
            >
              {title}
            </div>
            <div
              style={{
                fontSize: "13px",
                color: "var(--text-color)",
                opacity: 1,
                marginTop: "2px",
              }}
            >
              {message}
            </div>
          </div>
        </div>
        <div
          style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}
        >
          <button onClick={onCancel} style={S.btnOutline} disabled={confirming}>
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={confirming}
            style={{
              ...S.btnDanger,
              padding: "10px 20px",
              borderRadius: "10px",
              fontSize: "13px",
              opacity: confirming ? 0.7 : 1,
            }}
          >
            {confirming ? (
              <>
                <i className="mdi mdi-loading mdi-spin" /> Deleting…
              </>
            ) : (
              <>
                <i className="mdi mdi-delete" /> Delete
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <div style={S.card}>
      <div style={{ ...S.cardHeader, gap: "12px" }}>
        <div
          style={{
            ...S.skeleton,
            width: "36px",
            height: "36px",
            borderRadius: "10px",
          }}
        />
        <div
          style={{
            ...S.skeleton,
            width: "160px",
            height: "16px",
            borderRadius: "6px",
          }}
        />
      </div>
      <div
        style={{ ...S.cardBody, display: "flex", gap: "8px", flexWrap: "wrap" }}
      >
        {[100, 80, 120, 90].map((w, i) => (
          <div
            key={i}
            style={{
              ...S.skeleton,
              width: `${w}px`,
              height: "28px",
              borderRadius: "20px",
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ─── ProfileForm ──────────────────────────────────────────────────────────────
const EMPTY_CUSTOM = {
  id: null,
  customFieldId: null,
  label: "",
  type: "text",
  required: false,
  placeholder: "",
  helpText: "",
  options: "",
};

function ProfileForm({ initial, onSave, onCancel, saving }) {
  const [name, setName] = useState(initial?.name || "");
  const [stdFields, setStdFields] = useState(
    initial?.stdFields || buildDefaultStdFields()
  );
  const [customFields, setCustomFields] = useState(initial?.customFields || []);
  const [editingCustom, setEditingCustom] = useState(null);
  const [nameError, setNameError] = useState("");
  const [isDefaultProfile, setIsDefaultProfile] = useState(
    initial?.isDefaultProfile === true || initial?.isDefaultProfile === 1
  );

  const toggleField = (key, val) =>
    setStdFields((prev) =>
      prev.map((f) => (f.key === key ? { ...f, enabled: val } : f))
    );

  const addCustom = () => {
    const newField = {
      ...EMPTY_CUSTOM,
      id: Date.now(),
      customFieldId: customFields.length + 1,
    };
    setCustomFields((prev) => [...prev, newField]);
    setEditingCustom(customFields.length);
  };

  const updateCustom = (idx, field, val) =>
    setCustomFields((prev) =>
      prev.map((f, i) => (i === idx ? { ...f, [field]: val } : f))
    );

  const removeCustom = (idx) => {
    setCustomFields((prev) => prev.filter((_, i) => i !== idx));
    if (editingCustom === idx) setEditingCustom(null);
  };

  const handleSave = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError("Profile name is required");
      return;
    } else if (trimmedName.length < 2 || trimmedName.length > 100) {
      setNameError("Profile name must be 2–100 characters");
      return;
    } else if (!/^[a-zA-Z0-9 \-&]+$/.test(trimmedName)) {
      setNameError("Only letters, numbers, spaces, hyphens, and & are allowed");
      return;
    }
    setNameError("");
    onSave({
      name,
      stdFields,
      customFields,
      id: initial?.id,
      isDefaultProfile,
      createdAt: initial?.createdAt || null,
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <div style={S.formGroup}>
        <label style={S.label}>Profile Name *</label>
        <input
          style={{ ...S.input, ...(nameError ? { borderColor: "#ef4444" } : {}) }}
          value={name}
          onChange={(e) => { setName(e.target.value); if (nameError) setNameError(""); }}
          placeholder="e.g. Standard Checkout Form"
        />
        {nameError && <span style={{ fontSize: "12px", color: "#ef4444", marginTop: "2px" }}>{nameError}</span>}
      </div>

      <div>
        <div
          style={{
            fontSize: "13px",
            fontWeight: "700",
            color: "var(--text-color)",
            marginBottom: "14px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <i
            className="mdi mdi-format-list-checks"
            style={{ color: "var(--btn-color)" }}
          />
          Standard Fields
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {stdFields.map((f) => (
            <div
              key={f.key}
              style={{
                background: "var(--body-color)",
                borderRadius: "12px",
                border: `1px solid ${
                  f.enabled
                    ? "color-mix(in srgb, var(--btn-color) 25%, transparent)"
                    : "var(--body-color-less2)"
                }`,
                padding: "12px 16px",
                transition: "border-color 0.15s",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "9px",
                    background: f.enabled
                      ? "color-mix(in srgb, var(--btn-color) 12%, transparent)"
                      : "var(--body-color-less2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: f.enabled ? "var(--btn-color)" : "var(--text-color)",
                    opacity: 1,
                    fontSize: "15px",
                    flexShrink: 0,
                    transition: "all 0.15s",
                  }}
                >
                  <i className={`mdi ${f.icon}`} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "var(--text-color)",
                      opacity: 1,
                    }}
                  >
                    {f.label}
                    {f.locked && (
                      <span
                        style={{
                          marginLeft: "6px",
                          fontSize: "10px",
                          fontWeight: "700",
                          background:
                            "color-mix(in srgb, var(--btn-color) 12%, transparent)",
                          color: "var(--btn-color)",
                          borderRadius: "4px",
                          padding: "1px 6px",
                        }}
                      >
                        REQUIRED
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      color: "var(--text-color)",
                      opacity: 1,
                      marginTop: "1px",
                    }}
                  >
                    {f.helpText}
                  </div>
                </div>
                {!f.locked ? (
                  <Toggle
                    value={f.enabled}
                    onChange={(v) => toggleField(f.key, v)}
                  />
                ) : (
                  <div style={{ width: "36px" }} />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={S.divider} />

      <div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "14px",
          }}
        >
          <div
            style={{
              fontSize: "13px",
              fontWeight: "700",
              color: "var(--text-color)",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <i
              className="mdi mdi-plus-circle"
              style={{ color: "#8b5cf6" }}
            />
            Custom Fields
            {customFields.length > 0 && (
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: "600",
                  background: "rgba(139,92,246,0.12)",
                  color: "#8b5cf6",
                  borderRadius: "6px",
                  padding: "1px 7px",
                }}
              >
                {customFields.length}
              </span>
            )}
          </div>
          {customFields.length < MAX_CUSTOM_FIELDS && (
            <button type="button" onClick={addCustom} style={S.btnGhost}>
              <i className="mdi mdi-plus" /> Add Field
            </button>
          )}
        </div>

        {customFields.length === 0 && (
          <div
            style={{
              background: "var(--body-color)",
              borderRadius: "12px",
              border: "1px dashed var(--body-color-less2)",
              padding: "24px",
              textAlign: "center",
            }}
          >
            <i
              className="mdi mdi-form-textbox"
              style={{
                fontSize: "24px",
                color: "var(--text-color)",
                opacity: 1,
                display: "block",
                marginBottom: "8px",
              }}
            />
            <div
              style={{
                fontSize: "13px",
                color: "var(--text-color)",
                opacity: 1,
              }}
            >
              No custom fields yet — add up to {MAX_CUSTOM_FIELDS}
            </div>
          </div>
        )}

        {customFields.map((cf, idx) => (
          <div
            key={cf.id}
            style={{
              background: "var(--body-color)",
              borderRadius: "12px",
              border: "1px solid var(--body-color-less2)",
              marginBottom: "10px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "12px 16px",
                cursor: "pointer",
                background:
                  editingCustom === idx
                    ? "color-mix(in srgb, #8b5cf6, transparent 93%)"
                    : "transparent",
              }}
              onClick={() =>
                setEditingCustom(editingCustom === idx ? null : idx)
              }
            >
              <div
                style={{
                  width: "28px",
                  height: "28px",
                  borderRadius: "8px",
                  background: "rgba(139,92,246,0.12)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#8b5cf6",
                  fontSize: "13px",
                  flexShrink: 0,
                }}
              >
                <i className="mdi mdi-pencil" />
              </div>
              <div style={{ flex: 1 }}>
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "var(--text-color)",
                    opacity: 1,
                  }}
                >
                  {cf.label || (
                    <span style={{ opacity: 0.4 }}>Untitled Field</span>
                  )}
                </span>
                {cf.label && (
                  <span
                    style={{
                      marginLeft: "8px",
                      fontSize: "11px",
                      opacity: 0.5,
                      fontFamily: "'SF Mono','Fira Code',monospace",
                    }}
                  >
                    [{cf.type}]
                  </span>
                )}
              </div>
              <i
                className={`mdi mdi-chevron-${
                  editingCustom === idx ? "up" : "down"
                }`}
                style={{ opacity: 1, fontSize: "18px" }}
              />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeCustom(idx);
                }}
                style={{
                  width: "28px",
                  height: "28px",
                  borderRadius: "8px",
                  background: "rgba(239,68,68,0.1)",
                  border: "none",
                  color: "#ef4444",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <i className="mdi mdi-close" />
              </button>
            </div>

            {editingCustom === idx && (
              <div
                style={{
                  padding: "16px",
                  borderTop: "1px solid var(--body-color-less2)",
                  display: "flex",
                  flexDirection: "column",
                  gap: "14px",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "2fr 1fr",
                    gap: "14px",
                  }}
                >
                  <div style={S.formGroup}>
                    <label style={S.label}>Field Label *</label>
                    <input
                      style={S.input}
                      value={cf.label}
                      onChange={(e) =>
                        updateCustom(idx, "label", e.target.value)
                      }
                      placeholder="e.g. Company Registration No."
                    />
                  </div>
                  <div style={S.formGroup}>
                    <label style={S.label}>Field Type</label>
                    <select
                      style={S.select}
                      value={cf.type}
                      onChange={(e) =>
                        updateCustom(idx, "type", e.target.value)
                      }
                    >
                      {FIELD_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t.charAt(0).toUpperCase() + t.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "14px",
                  }}
                >
                  <div style={S.formGroup}>
                    <label style={S.label}>Placeholder Text</label>
                    <input
                      style={S.input}
                      value={cf.placeholder}
                      onChange={(e) =>
                        updateCustom(idx, "placeholder", e.target.value)
                      }
                      placeholder="Hint text for buyers"
                    />
                  </div>
                  <div style={S.formGroup}>
                    <label style={S.label}>Help Text</label>
                    <input
                      style={S.input}
                      value={cf.helpText}
                      onChange={(e) =>
                        updateCustom(idx, "helpText", e.target.value)
                      }
                      placeholder="Shown below the field"
                    />
                  </div>
                </div>
                {cf.type === "dropdown" && (
                  <div style={S.formGroup}>
                    <label style={S.label}>
                      Dropdown Options (comma-separated)
                    </label>
                    <input
                      style={S.input}
                      value={cf.options}
                      onChange={(e) =>
                        updateCustom(idx, "options", e.target.value)
                      }
                      placeholder="Option 1, Option 2, Option 3"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Default Profile Toggle */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          padding: "16px 20px",
          borderRadius: "12px",
          border: "1px solid var(--body-color-less2)",
          background: "var(--body-color)",
        }}
      >
        <div
          onClick={() => setIsDefaultProfile((prev) => !prev)}
          style={{
            width: "48px",
            height: "26px",
            borderRadius: "13px",
            background: isDefaultProfile ? "var(--btn-color)" : "rgba(150,150,150,0.3)",
            position: "relative",
            cursor: "pointer",
            flexShrink: 0,
            transition: "background 0.2s",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "3px",
              left: isDefaultProfile ? "25px" : "3px",
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              background: "#fff",
              transition: "left 0.2s",
              boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
            }}
          />
        </div>
        <div>
          <div style={{ fontSize: "14px", fontWeight: "600", color: "var(--text-color)" }}>
            Set as Default Profile
          </div>
          <div style={{ fontSize: "12px", color: "var(--text-color)", opacity: 1, marginTop: "2px" }}>
            This profile will be pre-selected at checkout
          </div>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
        <button
          type="button"
          onClick={onCancel}
          style={S.btnOutline}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          style={{ ...S.btnPrimary, opacity: saving ? 0.7 : 1 }}
          disabled={saving}
        >
          {saving ? (
            <>
              <i className="mdi mdi-loading mdi-spin" /> Saving…
            </>
          ) : (
            <>
              <i className="mdi mdi-check" /> Save Profile
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function BuyerInformation() {
  const isReadOnly = currentPageAccess() === "READ";
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("list"); // "list" | "add" | "edit"
  const [editing, setEditing] = useState(null);
  const [toast, setToast] = useState(null); // { message, ok }
  const [deleteTarget, setDeleteTarget] = useState(null); // profile to confirm-delete
  const [deleting, setDeleting] = useState(false);

  const merchantId = getMerchantId();

  // ── GET ──────────────────────────────────────────────────────────────────
  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await GetBuyerInformation(merchantId);
      if (res.data?.error === "0" && Array.isArray(res.data?.data)) {
        setProfiles(res.data.data.map(apiToProfile));
      } else {
        setProfiles([]);
      }
    } catch (err) {
      console.error("Failed to fetch buyer profiles:", err);
      setToast({ message: "Failed to load profiles", ok: false });
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  // ── CREATE (POST) / UPDATE (PUT) ─────────────────────────────────────────
  const handleSave = async (profile) => {
    if (isReadOnly) {
      setToast({ message: "You need write access to perform this operation", ok: false });
      return;
    }
    setSaving(true);
    try {
      let res;
      if (profile.id) {
        // Existing profile → PUT
        const payload = profileToUpdatePayload(profile, merchantId);
        res = await UpdateBuyerInformation(profile.id, payload);
      } else {
        // New profile → POST
        const payload = profileToCreatePayload(profile, merchantId);
        res = await SaveBuyerInformation(payload);
      }

      if (res.data?.error === "0") {
        setToast({
          message: `Profile ${profile.id ? "updated" : "created"} successfully`,
          ok: true,
        });
        setMode("list");
        setEditing(null);
        await fetchProfiles();
      } else {
        setToast({ message: res.data?.message || "Save failed", ok: false });
      }
    } catch (err) {
      console.error("Failed to save buyer profile:", err);
      setToast({ message: "Failed to save profile", ok: false });
    } finally {
      setSaving(false);
    }
  };

  // ── DELETE ───────────────────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    if (isReadOnly) {
      setToast({ message: "You need write access to perform this operation", ok: false });
      return;
    }
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await DeleteBuyerInformation(deleteTarget.id, merchantId);
      if (res.data?.error === "0") {
        setToast({ message: "Profile deleted", ok: true });
        setDeleteTarget(null);
        await fetchProfiles();
      } else {
        setToast({ message: res.data?.message || "Delete failed", ok: false });
      }
    } catch (err) {
      console.error("Failed to delete buyer profile:", err);
      setToast({ message: "Failed to delete profile", ok: false });
    } finally {
      setDeleting(false);
    }
  };

  const handleCancel = () => {
    setMode("list");
    setEditing(null);
  };

  return (
    <Fragment>
      <style>{`
                @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.45} }
                @keyframes slideUp { from{transform:translateY(16px);opacity:0} to{transform:translateY(0);opacity:1} }
            `}</style>

      {toast && (
        <Toast
          message={toast.message}
          ok={toast.ok}
          onClose={() => setToast(null)}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Delete Profile"
          message={`"${deleteTarget.name}" will be permanently removed.`}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
          confirming={deleting}
        />
      )}

      <div className="container-scroller">
        <Sidebar />
        <Header />
        <div className="main-panel">
          <div style={S.pageWrapper}>
            {/* ── Page Header ── */}
            <div style={S.headerRow}>
              <div>
                {(mode === "add" || mode === "edit") && (
                  <span
                    onClick={handleCancel}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 13,
                      fontWeight: 600,
                      color: "var(--btn-color)",
                      cursor: "pointer",
                      marginBottom: 8,
                      userSelect: "none",
                      opacity: 0.85,
                      transition: "opacity .15s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = 1;
                      e.currentTarget.style.textDecoration = "underline";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = 0.85;
                      e.currentTarget.style.textDecoration = "none";
                    }}
                  >
                    <i
                      className="mdi mdi-arrow-left"
                      style={{ fontSize: 14 }}
                    />
                    Back to Buyer Info
                  </span>
                )}
                <h2 style={S.pageTitle}>
                  {mode === "add"
                    ? "Create Buyer Info Profile"
                    : mode === "edit"
                    ? `Edit — ${editing?.name}`
                    : "Buyer Info"}
                </h2>
                {mode === "list" && (
                  <div style={S.pageSubtitle}>
                    Configure what data to collect from buyers at checkout
                  </div>
                )}
              </div>
              {mode === "list" && !loading && !isReadOnly && (
                <button
                  style={S.btnPrimary}
                  onClick={() => {
                    setEditing(null);
                    setMode("add");
                  }}
                >
                  <i className="mdi mdi-plus" /> Add Profile
                </button>
              )}
            </div>

            {/* ── Add / Edit Form ── */}
            {(mode === "add" || mode === "edit") && (
              <div style={S.card}>
                <div style={S.cardHeader}>
                  <div
                    style={{
                      ...S.cardHeaderIcon,
                      background:
                        "linear-gradient(135deg, color-mix(in srgb, #8b5cf6 15%, transparent), color-mix(in srgb, #8b5cf6 5%, transparent))",
                      color: "#8b5cf6",
                    }}
                  >
                    <i className="mdi mdi-account-edit" />
                  </div>
                  <h4 style={S.cardHeaderTitle}>
                    {mode === "add"
                      ? "New Buyer Info Profile"
                      : `Edit: ${editing?.name}`}
                  </h4>
                </div>
                <div style={S.cardBody}>
                  <ProfileForm
                    initial={editing}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    saving={saving}
                  />
                </div>
              </div>
            )}

            {/* ── Loading Skeletons ── */}
            {mode === "list" && loading && (
              <>
                <ProfileSkeleton />
                <ProfileSkeleton />
              </>
            )}

            {/* ── Profile List ── */}
            {mode === "list" &&
              !loading &&
              profiles.map((p) => (
                <div key={p.id} style={S.card}>
                  <div style={S.cardHeader}>
                    <div
                      style={{
                        ...S.cardHeaderIcon,
                        background:
                          "linear-gradient(135deg, color-mix(in srgb, #8b5cf6 15%, transparent), color-mix(in srgb, #8b5cf6 5%, transparent))",
                        color: "#8b5cf6",
                      }}
                    >
                      <i className="mdi mdi-account-check" />
                    </div>
                    <h4 style={S.cardHeaderTitle}>{p.name}</h4>
                    <span
                      style={{
                        marginLeft: "8px",
                        fontSize: "12px",
                        color: "var(--text-color)",
                        opacity: 1,
                        fontWeight: "600",
                      }}
                    >
                      {p.stdFields.filter((f) => f.enabled).length} std +{" "}
                      {p.customFields.length} custom field
                      {p.customFields.length !== 1 ? "s" : ""}
                    </span>
                    {p.isDefaultProfile && (
                      <span
                        style={{
                          padding: "4px 12px",
                          borderRadius: "20px",
                          fontSize: "11px",
                          fontWeight: "700",
                          background: "rgba(59,130,246,0.12)",
                          color: "#3b82f6",
                          border: "1px solid rgba(59,130,246,0.25)",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      >
                        <i className="mdi mdi-star" style={{ fontSize: "12px" }} />
                        Default
                      </span>
                    )}
                    {!isReadOnly && (
                      <div
                        style={{
                          flex: "1 0 100%",
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "8px",
                        }}
                      >
                        <button
                          onClick={() => {
                            setEditing(p);
                            setMode("edit");
                          }}
                          style={S.btnOutline}
                        >
                          <i className="mdi mdi-pencil" /> Edit
                        </button>
                        <button
                          onClick={() => setDeleteTarget(p)}
                          style={S.btnDanger}
                        >
                          <i className="mdi mdi-delete" />
                        </button>
                      </div>
                    )}
                  </div>
                  <div style={S.cardBody}>
                    <div
                      style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}
                    >
                      {p.stdFields
                        .filter((f) => f.enabled)
                        .map((f) => (
                          <span
                            key={f.key}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "5px",
                              padding: "4px 12px",
                              borderRadius: "20px",
                              fontSize: "12px",
                              fontWeight: "600",
                              background:
                                "color-mix(in srgb, var(--btn-color) 10%, transparent)",
                              color: "var(--btn-color)",
                              border:
                                "1px solid color-mix(in srgb, var(--btn-color) 20%, transparent)",
                            }}
                          >
                            <i
                              className={`mdi ${f.icon}`}
                              style={{ fontSize: "12px" }}
                            />{" "}
                            {f.label}
                          </span>
                        ))}
                      {p.customFields.map((f, i) => (
                        <span
                          key={f.id || i}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px",
                            padding: "4px 12px",
                            borderRadius: "20px",
                            fontSize: "12px",
                            fontWeight: "600",
                            background: "rgba(139,92,246,0.1)",
                            color: "#8b5cf6",
                            border: "1px solid rgba(139,92,246,0.2)",
                          }}
                        >
                          <i
                            className="mdi mdi-pencil"
                            style={{ fontSize: "12px" }}
                          />{" "}
                          {f.label || "Unnamed"}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}

            {/* ── Empty State ── */}
            {mode === "list" && !loading && profiles.length === 0 && (
              <div style={S.card}>
                <div style={{ textAlign: "center", padding: "48px 24px" }}>
                  <div
                    style={{
                      width: "56px",
                      height: "56px",
                      borderRadius: "16px",
                      background:
                        "linear-gradient(135deg, rgba(139,92,246,0.12), rgba(139,92,246,0.04))",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 16px",
                      fontSize: "24px",
                      color: "#8b5cf6",
                    }}
                  >
                    <i className="mdi mdi-account" />
                  </div>
                  <div
                    style={{
                      fontSize: "15px",
                      fontWeight: "600",
                      color: "var(--text-color)",
                      marginBottom: "6px",
                    }}
                  >
                    No buyer info profiles
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      color: "var(--text-color)",
                      opacity: 1,
                    }}
                  >
                    Create a profile to define what information to collect
                  </div>
                  {!isReadOnly && (
                    <button
                      style={{ ...S.btnPrimary, marginTop: "18px" }}
                      onClick={() => {
                        setEditing(null);
                        setMode("add");
                      }}
                    >
                      <i className="mdi mdi-plus" /> Add Buyer Info
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
          <Footer />
        </div>
      </div>
    </Fragment>
  );
}
